import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import api from '../utils/axios';
import { toast } from 'sonner';
import { usePermisos } from '../hooks/usePermisos';
import {
  ShieldAlert, ShieldCheck, ShieldX, RefreshCw, KeyRound, Copy, Plus,
  RotateCw, Ban, Loader2, ListChecks, Clock, CheckCircle2,
  Hash, FileCode2, Bot, Info, AlertTriangle, FlaskConical, Power,
  Play, Trash2, XCircle,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ENDPOINT_URL = `${BACKEND}/api/v1/security/account-blocks`;

const fmtFecha = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const RESULT_META = {
  BLOCKED: { label: 'Bloqueada', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  ALREADY_BLOCKED: { label: 'Ya bloqueada', cls: 'bg-sky-100 text-sky-700 border-sky-200' },
  ACCOUNT_NOT_VERIFIED: { label: 'No verificada', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const KPI = ({ label, value, icon: Icon, color = '#0f766e' }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="seg-kpi">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{label}</p>
        <p className="mt-1 text-2xl font-extrabold tabular-nums" style={{ color }}>{value}</p>
      </div>
      {Icon && <div className="rounded-lg p-2" style={{ backgroundColor: `${color}15` }}><Icon size={18} style={{ color }} /></div>}
    </div>
  </div>
);

export default function Seguridad({ mode = 'dashboard' }) {
  const { isAdmin } = usePermisos();
  const admin = isAdmin();
  const isCredentials = mode === 'credentials';

  const [tab, setTab] = useState('solicitudes');
  const [stats, setStats] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loadingSol, setLoadingSol] = useState(false);

  const [creds, setCreds] = useState([]);
  const [loadingCreds, setLoadingCreds] = useState(false);

  const [execMode, setExecMode] = useState(null);
  const [savingMode, setSavingMode] = useState(false);

  const [execModal, setExecModal] = useState(null); // {rowId, jobId, nombre}
  const [execProgress, setExecProgress] = useState(null); // {status, step, step_index, total, result}
  const pollRef = useRef(null);
  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const [showCrear, setShowCrear] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoRate, setNuevoRate] = useState(30);
  const [creando, setCreando] = useState(false);
  const [tokenNuevo, setTokenNuevo] = useState(null); // {token, credential_id, nombre}

  const loadSolicitudes = useCallback(async () => {
    setLoadingSol(true);
    try {
      const [s, l, m] = await Promise.all([
        api.get('/security/account-blocks/stats'),
        api.get('/security/account-blocks?limit=100'),
        api.get('/security/execution-mode'),
      ]);
      setStats(s.data);
      setSolicitudes(l.data.solicitudes || []);
      setExecMode(m.data.mode);
    } catch (e) {
      toast.error('No se pudieron cargar las solicitudes de bloqueo');
    } finally { setLoadingSol(false); }
  }, []);

  const toggleMode = async (toLive) => {
    const target = toLive ? 'LIVE' : 'TEST';
    if (target === 'LIVE' && !window.confirm(
      '¿Activar MODO ACTIVO? A partir de ahora cada solicitud de Freddy rechazará la validación de identidad REAL en Waya. ¿Continuar?'
    )) return;
    setSavingMode(true);
    try {
      const r = await api.post('/security/execution-mode', { mode: target });
      setExecMode(r.data.mode);
      toast.success(target === 'LIVE' ? 'Modo ACTIVO: los bloqueos se ejecutan en Waya.' : 'Modo PRUEBA: los bloqueos quedan suspendidos.');
    } catch (e) {
      toast.error('No se pudo cambiar el modo de ejecución');
    } finally { setSavingMode(false); }
  };

  const ejecutarSolicitud = async (s) => {
    if (!window.confirm(`¿Ejecutar el bloqueo REAL en Waya para ${s.razon_social || s.cuit || 'esta cuenta'}? Se rechazará la validación de identidad de la cuenta.`)) return;
    try {
      const r = await api.post(`/security/account-blocks/${s.id}/execute`);
      const jobId = r.data.job_id;
      setExecModal({ rowId: s.id, jobId, nombre: s.razon_social || s.cuit || '' });
      setExecProgress({ status: 'RUNNING', step: 'En cola…', step_index: 0, total: 6 });
      stopPoll();
      pollRef.current = setInterval(async () => {
        try {
          const j = await api.get(`/security/account-blocks/execute/${jobId}`);
          setExecProgress(j.data);
          if (j.data.status === 'DONE' || j.data.status === 'ERROR') {
            stopPoll();
            loadSolicitudes();
          }
        } catch (e) { /* seguimos intentando */ }
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo iniciar la ejecución');
    }
  };

  const eliminarSolicitud = async (s) => {
    if (!window.confirm('¿Eliminar esta solicitud del listado? Esta acción no se puede deshacer.')) return;
    try {
      await api.delete(`/security/account-blocks/${s.id}`);
      toast.success('Solicitud eliminada');
      loadSolicitudes();
    } catch (e) { toast.error('No se pudo eliminar la solicitud'); }
  };

  useEffect(() => () => stopPoll(), []);

  const loadCreds = useCallback(async () => {
    setLoadingCreds(true);
    try {
      const r = await api.get('/security/credentials');
      setCreds(r.data.credenciales || []);
    } catch (e) {
      toast.error('No se pudieron cargar las credenciales');
    } finally { setLoadingCreds(false); }
  }, []);

  useEffect(() => {
    if (isCredentials) { loadCreds(); }
    else { loadSolicitudes(); }
  }, [isCredentials, loadCreds, loadSolicitudes]);

  const crearCredencial = async () => {
    if (!nuevoNombre.trim()) { toast.error('Ingresá un nombre para la credencial'); return; }
    setCreando(true);
    try {
      const r = await api.post('/security/credentials', {
        nombre: nuevoNombre.trim(), rate_limit: Number(nuevoRate) || 30,
      });
      setTokenNuevo({ token: r.data.token, credential_id: r.data.credential_id, nombre: r.data.nombre });
      setShowCrear(false);
      setNuevoNombre('');
      setNuevoRate(30);
      await loadCreds();
      toast.success('Credencial creada. Copiá el token ahora, no se vuelve a mostrar.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la credencial');
    } finally { setCreando(false); }
  };

  const rotarCredencial = async (id) => {
    if (!window.confirm('¿Rotar el token? El token anterior dejará de funcionar de inmediato.')) return;
    try {
      const r = await api.post(`/security/credentials/${id}/rotate`);
      setTokenNuevo({ token: r.data.token, credential_id: id, nombre: '(rotada)' });
      await loadCreds();
      toast.success('Token rotado. Copiá el nuevo token ahora.');
    } catch (e) { toast.error('No se pudo rotar la credencial'); }
  };

  const revocarCredencial = async (id) => {
    if (!window.confirm('¿Revocar esta credencial? Freddy no podrá autenticar con ella.')) return;
    try {
      await api.post(`/security/credentials/${id}/revoke`);
      await loadCreds();
      toast.success('Credencial revocada');
    } catch (e) { toast.error('No se pudo revocar la credencial'); }
  };

  const copiar = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success('Copiado al portapapeles');
  };

  // ---------- Bloques reutilizables ----------
  const CredencialesCard = (
    <Card className="p-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <KeyRound size={15} className="text-teal-600" /> Credenciales M2M (Bearer token)
        </h3>
        {admin && (
          <Button size="sm" onClick={() => setShowCrear(true)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="seg-cred-crear-btn">
            <Plus size={15} className="mr-1.5" /> Generar token
          </Button>
        )}
      </div>
      {!admin && (
        <div className="mx-4 mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="seg-cred-readonly">
          <Info size={14} className="mt-0.5" /> Solo un Administrador puede generar, rotar o revocar tokens.
        </div>
      )}
      {loadingCreds ? (
        <div className="p-14 text-center text-slate-400"><Loader2 className="mx-auto mb-2 animate-spin" /> Cargando…</div>
      ) : creds.length === 0 ? (
        <div className="p-14 text-center text-slate-400" data-testid="seg-cred-empty">
          No hay credenciales creadas. {admin && 'Generá un token para que Freddy pueda autenticar.'}
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="seg-cred-tabla">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="p-3">Nombre</th>
                <th className="p-3">Credential ID</th>
                <th className="p-3">Scopes</th>
                <th className="p-3 text-center">Rate limit</th>
                <th className="p-3 text-center">Estado</th>
                <th className="p-3">Creada</th>
                <th className="p-3">Último uso</th>
                {admin && <th className="p-3 text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {creds.map((c, i) => (
                <tr key={c.credential_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`seg-cred-row-${i}`}>
                  <td className="p-3 font-medium text-slate-800">{c.nombre}</td>
                  <td className="p-3 font-mono text-[11px] text-slate-500">{c.credential_id}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(c.scopes || []).map((sc) => <Badge key={sc} className="bg-slate-100 text-slate-600 border border-slate-200 text-[10px]">{sc}</Badge>)}
                    </div>
                  </td>
                  <td className="p-3 text-center tabular-nums text-slate-600">{c.rate_limit}/min</td>
                  <td className="p-3 text-center">
                    {c.activo && !c.revoked_at
                      ? <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">Activa</Badge>
                      : <Badge className="bg-rose-100 text-rose-700 border border-rose-200">Revocada</Badge>}
                  </td>
                  <td className="p-3 whitespace-nowrap text-xs text-slate-500">{fmtFecha(c.created_at)}</td>
                  <td className="p-3 whitespace-nowrap text-xs text-slate-500">{c.last_used_at ? fmtFecha(c.last_used_at) : '—'}</td>
                  {admin && (
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="h-8" onClick={() => rotarCredencial(c.credential_id)} data-testid={`seg-cred-rotar-${i}`}>
                          <RotateCw size={13} className="mr-1" /> Rotar
                        </Button>
                        {(c.activo && !c.revoked_at) && (
                          <Button size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => revocarCredencial(c.credential_id)} data-testid={`seg-cred-revocar-${i}`}>
                            <Ban size={13} className="mr-1" /> Revocar
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );

  const DialogsCreds = (
    <>
      <Dialog open={showCrear} onOpenChange={setShowCrear}>
        <DialogContent data-testid="seg-cred-crear-dialog">
          <DialogHeader>
            <DialogTitle>Generar credencial M2M</DialogTitle>
            <DialogDescription>El token se mostrará una sola vez. Copialo y compartilo de forma segura con el equipo de Freddy.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="seg-nombre">Nombre de la credencial</Label>
              <Input id="seg-nombre" value={nuevoNombre} onChange={(e) => setNuevoNombre(e.target.value)}
                placeholder="Ej: Freddy Producción" className="mt-1" data-testid="seg-cred-nombre-input" />
            </div>
            <div>
              <Label htmlFor="seg-rate">Rate limit (solicitudes por minuto)</Label>
              <Input id="seg-rate" type="number" min={1} value={nuevoRate} onChange={(e) => setNuevoRate(e.target.value)}
                className="mt-1 w-40" data-testid="seg-cred-rate-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCrear(false)} data-testid="seg-cred-cancelar-btn">Cancelar</Button>
            <Button onClick={crearCredencial} disabled={creando} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="seg-cred-confirmar-btn">
              {creando ? <Loader2 size={15} className="mr-2 animate-spin" /> : <KeyRound size={15} className="mr-2" />} Generar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tokenNuevo} onOpenChange={(o) => { if (!o) setTokenNuevo(null); }}>
        <DialogContent data-testid="seg-token-dialog">
          <DialogHeader>
            <DialogTitle>Token generado</DialogTitle>
            <DialogDescription>Guardalo ahora. Por seguridad no se puede volver a mostrar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {tokenNuevo?.nombre && <p className="text-xs text-slate-500">Credencial: <span className="font-medium text-slate-700">{tokenNuevo.nombre}</span></p>}
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <code className="flex-1 font-mono text-xs break-all text-slate-800" data-testid="seg-token-valor">{tokenNuevo?.token}</code>
              <button onClick={() => copiar(tokenNuevo?.token)} className="text-teal-600 hover:text-teal-800" title="Copiar" data-testid="seg-token-copiar-btn"><Copy size={16} /></button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setTokenNuevo(null)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="seg-token-cerrar-btn">Listo, lo guardé</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // ==================== MODO CREDENCIALES (Configuración) ====================
  if (isCredentials) {
    return (
      <div className="px-6 pb-6 pt-20 space-y-5" data-testid="seguridad-page">
        <div className="bcra-header p-5 rounded-xl shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-lg p-2" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}><KeyRound size={22} /></div>
              <div>
                <h2 className="text-lg font-extrabold leading-tight">Credenciales M2M</h2>
                <p className="text-xs opacity-90">Generá, rotá y revocá los tokens Bearer que usa el chatbot para autenticar</p>
              </div>
            </div>
            <Button onClick={loadCreds} variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-white/30" data-testid="seg-refresh-btn">
              <RefreshCw size={15} className="mr-2" /> Actualizar
            </Button>
          </div>
        </div>
        {CredencialesCard}
        {DialogsCreds}
      </div>
    );
  }

  // ==================== MODO DASHBOARD (módulo Seguridad) ====================
  const ep = execProgress || {};
  const epTotal = ep.total || 6;
  const epPct = Math.round(((ep.step_index || 0) / epTotal) * 100);
  const epResult = ep.result || {};
  const RESULT_DONE = {
    BLOCKED: { cls: 'text-emerald-700', icon: CheckCircle2, txt: 'Identidad rechazada en Waya' },
    ALREADY_BLOCKED: { cls: 'text-sky-700', icon: CheckCircle2, txt: 'La cuenta ya estaba rechazada' },
    ACCOUNT_NOT_VERIFIED: { cls: 'text-amber-700', icon: AlertTriangle, txt: 'No se encontró la cuenta (CUIT + email)' },
    INTERNAL_ERROR: { cls: 'text-rose-700', icon: XCircle, txt: 'No se pudo ejecutar el cambio' },
  };
  return (
    <div className="px-6 pb-6 pt-20 space-y-5" data-testid="seguridad-page">
      <div className="bcra-header p-5 rounded-xl shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg p-2" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}><ShieldAlert size={22} /></div>
            <div>
              <h2 className="text-lg font-extrabold leading-tight">Bloqueos Freddy</h2>
              <p className="text-xs opacity-90">Solicitudes de bloqueo preventivo recibidas del chatbot (Freddy / Freshchat)</p>
            </div>
          </div>
          <Button onClick={loadSolicitudes} variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-white/30" data-testid="seg-refresh-btn">
            <RefreshCw size={15} className="mr-2" /> Actualizar
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="seg-tabs">
          <TabsTrigger value="solicitudes" data-testid="seg-tab-solicitudes">
            <ListChecks size={15} className="mr-1.5" /> Solicitudes
          </TabsTrigger>
          <TabsTrigger value="documentacion" data-testid="seg-tab-documentacion">
            <FileCode2 size={15} className="mr-1.5" /> Documentación
          </TabsTrigger>
        </TabsList>

        {/* ===================== SOLICITUDES ===================== */}
        <TabsContent value="solicitudes" className="space-y-4 mt-4">
          {/* Switch de modo de ejecución */}
          <div className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${execMode === 'LIVE' ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}`} data-testid="seg-mode-banner">
            <div className="flex items-start gap-3">
              {execMode === 'LIVE'
                ? <Power size={20} className="text-rose-600 mt-0.5" />
                : <FlaskConical size={20} className="text-amber-600 mt-0.5" />}
              <div>
                <p className="text-sm font-bold text-slate-800">
                  Modo de ejecución: {execMode === 'LIVE'
                    ? <span className="text-rose-700">ACTIVO</span>
                    : <span className="text-amber-700">PRUEBA</span>}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 max-w-xl">
                  {execMode === 'LIVE'
                    ? 'Cada solicitud de Freddy entra a Waya y rechaza la validación de identidad de la cuenta (ejecución real).'
                    : 'Cada solicitud entra a Waya, ubica la cuenta y valida el flujo, pero deja el bloqueo SUSPENDIDO (no se guarda el rechazo).'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-amber-700">Prueba</span>
              <Switch
                checked={execMode === 'LIVE'}
                onCheckedChange={toggleMode}
                disabled={!admin || savingMode || execMode === null}
                data-testid="seg-mode-switch"
              />
              <span className="text-xs font-medium text-rose-700">Activo</span>
            </div>
          </div>
          {!admin && (
            <p className="text-[11px] text-slate-400 -mt-2" data-testid="seg-mode-readonly">Solo un Administrador puede cambiar el modo de ejecución.</p>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KPI label="Total" value={stats?.total ?? '—'} icon={ListChecks} color="#0f766e" />
            <KPI label="Bloqueadas" value={stats?.bloqueadas ?? '—'} icon={ShieldCheck} color="#059669" />
            <KPI label="Ya bloqueadas" value={stats?.ya_bloqueadas ?? '—'} icon={CheckCircle2} color="#0284c7" />
            <KPI label="No verificadas" value={stats?.no_verificadas ?? '—'} icon={ShieldX} color="#d97706" />
            <KPI label="Suspendidas" value={stats?.pendientes_ejecucion ?? '—'} icon={Clock} color="#7c3aed" />
          </div>

          <Card className="p-0 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Bot size={15} className="text-teal-600" /> Solicitudes recibidas de Freddy
              </h3>
              <span className="text-[11px] text-slate-400">{solicitudes.length} registro(s)</span>
            </div>
            {loadingSol ? (
              <div className="p-14 text-center text-slate-400" data-testid="seg-sol-loading">
                <Loader2 className="mx-auto mb-2 animate-spin" /> Cargando…
              </div>
            ) : solicitudes.length === 0 ? (
              <div className="p-14 text-center text-slate-400" data-testid="seg-sol-empty">
                Todavía no llegaron solicitudes de bloqueo desde el chatbot.
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="seg-sol-tabla">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                      <th className="p-3">Fecha</th>
                      <th className="p-3">Canal</th>
                      <th className="p-3">Ticket Freshdesk</th>
                      <th className="p-3">Resultado</th>
                      <th className="p-3">Ejecución</th>
                      <th className="p-3">Cliente</th>
                      <th className="p-3">CUIT</th>
                      <th className="p-3">Email</th>
                      <th className="p-3">Operation ID</th>
                      {admin && <th className="p-3 text-right">Acciones</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {solicitudes.map((s, i) => {
                      const meta = RESULT_META[s.result] || { label: s.result || '—', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                      return (
                        <tr key={s.id || i} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`seg-sol-row-${i}`}>
                          <td className="p-3 whitespace-nowrap text-slate-600">{fmtFecha(s.created_at)}</td>
                          <td className="p-3"><span className="text-xs font-medium text-slate-700">{s.channel || '—'}</span></td>
                          <td className="p-3">
                            {s.ticket_id
                              ? <span className="inline-flex items-center gap-1 font-mono text-xs text-slate-700"><Hash size={11} className="text-slate-400" />{s.ticket_id}</span>
                              : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="p-3"><Badge className={`border ${meta.cls}`}>{meta.label}</Badge></td>
                          <td className="p-3">
                            {s.execution_status === 'EXECUTED'
                              ? <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px]">Ejecutado</Badge>
                              : s.execution_status === 'SUSPENDED'
                                ? <Badge className="bg-amber-100 text-amber-700 border border-amber-200 text-[10px]">Suspendido{s.execution_mode === 'TEST' ? ' (prueba)' : ''}</Badge>
                                : s.execution_status === 'FAILED'
                                  ? <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[10px]">Falló</Badge>
                                  : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="p-3 text-xs text-slate-700 max-w-[180px] truncate" title={s.razon_social || ''}>{s.razon_social || '—'}</td>
                          <td className="p-3 font-mono text-xs text-slate-700">{s.cuit || s.cuit_masked || '—'}</td>
                          <td className="p-3 text-xs text-slate-700">{s.email || s.email_masked || '—'}</td>
                          <td className="p-3 font-mono text-[11px] text-slate-500">{s.operation_id || '—'}</td>
                          {admin && (
                            <td className="p-3">
                              <div className="flex items-center justify-end gap-1.5">
                                {s.waya_account_id && s.execution_status !== 'EXECUTED' && (
                                  <Button size="sm" variant="outline" className="h-8 text-teal-700 border-teal-200 hover:bg-teal-50"
                                    onClick={() => ejecutarSolicitud(s)} data-testid={`seg-sol-ejecutar-${i}`}>
                                    <Play size={13} className="mr-1" /> Ejecutar
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50"
                                  onClick={() => eliminarSolicitud(s)} data-testid={`seg-sol-eliminar-${i}`}>
                                  <Trash2 size={13} />
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ===================== DOCUMENTACIÓN ===================== */}
        <TabsContent value="documentacion" className="space-y-4 mt-4">
          <Card className="p-5 space-y-4" data-testid="seg-doc">
            <div>
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2"><FileCode2 size={15} className="text-teal-600" /> API de bloqueo preventivo (para el equipo de Freddy / Freshchat)</h3>
              <p className="text-xs text-slate-500 mt-1">Endpoint REST máquina-a-máquina. Autenticación por Bearer token (scope <code className="font-mono">account:block</code>), idempotente por <code className="font-mono">request_id</code>, rate limit configurable por credencial (default 30/min). El token se genera en <b>Configuración → Credenciales M2M</b>. Al recibir la solicitud, AlertGuard entra a Waya, busca por CUIT + email (deben coincidir ambos) y rechaza la validación de identidad de esa cuenta.</p>
            </div>

            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <div className="flex items-center gap-2 bg-slate-50 px-3 py-2 border-b border-slate-100">
                <Badge className="bg-teal-600 text-white">POST</Badge>
                <code className="font-mono text-xs text-slate-700 break-all" data-testid="seg-doc-url">{ENDPOINT_URL}</code>
                <button onClick={() => copiar(ENDPOINT_URL)} className="ml-auto text-slate-400 hover:text-slate-700" title="Copiar"><Copy size={14} /></button>
              </div>
              <div className="p-3 space-y-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Headers</p>
                  <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] overflow-x-auto font-mono">{`Authorization: Bearer <token>
Content-Type: application/json`}</pre>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Body (JSON)</p>
                  <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] overflow-x-auto font-mono">{`{
  "cuit": "20299507107",
  "email": "titular@dominio.com",
  "reason": "Bloqueo preventivo solicitado por el titular",
  "channel": "FREDDY_WHATSAPP",
  "request_id": "7f1c9a2e-3b4d-4a5e-8c6f-1a2b3c4d5e6f",
  "ticket_id": "123456"
}`}</pre>
                  <p className="text-[11px] text-slate-500 mt-1"><code className="font-mono">request_id</code> debe ser un UUID v4 (clave de idempotencia). <code className="font-mono">ticket_id</code> es el N° de ticket de Freshdesk (opcional, se devuelve en la respuesta). La identidad se valida en Waya: la cuenta debe coincidir en CUIT <b>y</b> email.</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Respuesta 200 (OK)</p>
                  <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] overflow-x-auto font-mono">{`{
  "success": true,
  "status": "BLOCKED",
  "message": "Cuenta bloqueada preventivamente.",
  "operation_id": "BLK-XXXXXXXXXXXXXXXXXXXX",
  "request_id": "7f1c9a2e-...",
  "ticket_id": "4532",
  "execution_mode": "TEST",
  "timestamp": "2026-06-01T12:00:00+00:00"
}`}</pre>
                  <p className="text-[11px] text-slate-500 mt-1"><code className="font-mono">execution_mode</code>: <b>TEST</b> = se hace todo en Waya pero el rechazo queda suspendido (no se guarda); <b>LIVE</b> = se rechaza la identidad de verdad. En ambos casos con éxito devuelve <code className="font-mono">status: "BLOCKED"</code> y <code className="font-mono">success: true</code>.</p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Códigos de respuesta</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-slate-100">
                    {[
                      ['200', 'BLOCKED / ALREADY_BLOCKED', 'Bloqueo registrado (o ya estaba bloqueada).'],
                      ['400', 'INVALID_REQUEST', 'Faltan campos o el formato es inválido (CUIT, email, request_id).'],
                      ['401', 'UNAUTHORIZED', 'Token ausente, inválido o revocado.'],
                      ['403', 'FORBIDDEN', 'El token no tiene el scope account:block.'],
                      ['422', 'ACCOUNT_NOT_VERIFIED', 'CUIT + email no coinciden con una cuenta en Waya.'],
                      ['429', 'RATE_LIMITED', 'Se superó el límite de solicitudes por minuto.'],
                      ['500', 'INTERNAL_ERROR', 'Error interno; reintentar más tarde.'],
                    ].map(([code, st, desc]) => (
                      <tr key={code}>
                        <td className="py-2 pr-3 font-mono font-bold text-slate-700 whitespace-nowrap">{code}</td>
                        <td className="py-2 pr-3 font-mono text-slate-600 whitespace-nowrap">{st}</td>
                        <td className="py-2 text-slate-500">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Ejemplo con curl</p>
              <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] overflow-x-auto font-mono">{`curl -X POST "${ENDPOINT_URL}" \\
  -H "Authorization: Bearer <TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{"cuit":"20299507107","email":"titular@dominio.com","reason":"Bloqueo preventivo","channel":"FREDDY_WHATSAPP","request_id":"7f1c9a2e-3b4d-4a5e-8c6f-1a2b3c4d5e6f","ticket_id":"123456"}'`}</pre>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de progreso de ejecución individual */}
      <Dialog open={!!execModal} onOpenChange={(o) => { if (!o) { stopPoll(); setExecModal(null); setExecProgress(null); } }}>
        <DialogContent data-testid="seg-exec-dialog">
          <DialogHeader>
            <DialogTitle>Ejecutar bloqueo en Waya</DialogTitle>
            <DialogDescription>{execModal?.nombre || 'Cuenta'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Progress value={ep.status === 'ERROR' ? 100 : epPct} data-testid="seg-exec-progress" />
            <div className="flex items-center gap-2 text-sm" data-testid="seg-exec-step">
              {ep.status === 'RUNNING' && <Loader2 size={16} className="animate-spin text-teal-600" />}
              {ep.status === 'DONE' && (() => { const M = RESULT_DONE[epResult.status] || RESULT_DONE.INTERNAL_ERROR; const I = M.icon; return <I size={16} className={M.cls} />; })()}
              {ep.status === 'ERROR' && <XCircle size={16} className="text-rose-600" />}
              <span className="font-medium text-slate-700">
                {ep.status === 'DONE'
                  ? (RESULT_DONE[epResult.status]?.txt || 'Finalizado')
                  : ep.status === 'ERROR'
                    ? 'Error al ejecutar en Waya'
                    : (ep.step || 'Procesando…')}
              </span>
            </div>
            {ep.status === 'RUNNING' && (
              <p className="text-xs text-slate-400">Paso {ep.step_index || 0} de {epTotal}</p>
            )}
            {ep.status === 'DONE' && epResult.validacion_identidad && (
              <p className="text-xs text-slate-500">Validación de identidad en Waya: <b>{epResult.validacion_identidad}</b></p>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => { stopPoll(); setExecModal(null); setExecProgress(null); }}
              disabled={ep.status === 'RUNNING'}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="seg-exec-cerrar-btn"
            >
              {ep.status === 'RUNNING' ? 'Ejecutando…' : 'Cerrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {DialogsCreds}
    </div>
  );
}
